import React, { useState } from 'react'
import parse, { domToReact } from 'html-react-parser'
import { Typography } from '~/components/_atoms'
import { useTranslation } from '~/hooks/useTranslation'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/lib/ui/table'
import { columns } from './columns'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useIsDesktop } from '~/hooks/useMediaQuery'
import { SortIcon } from '~/components/job_search/AlertsPage/icons'
import { cn } from '~/lib/utils'

const DataTable = ({
  chatHistories,
  setChatHistories,
  setSelectedChat,
  toast,
}) => {
  const { t } = useTranslation()

  const isDesktop = useIsDesktop()
  const desktopVisibility = {
    name: true,
    started_at: true,
    chat_type: true,
    view: true,
    email: true,
    download: true,
    delete: true,
  }

  const mobileVisibility = {
    name: true,
    started_at: false,
    chat_type: false,
    view: false,
    email: false,
    download: false,
    delete: false,
  }
  const [sorting, setSorting] = useState([])
  const [columnFilters, setColumnFilters] = React.useState([])

  const [columnVisibility, setColumnVisibility] = React.useState(
    isDesktop ? desktopVisibility : mobileVisibility,
  )

  React.useEffect(() => {
    if (isDesktop) {
      setColumnVisibility({
        name: true,
        started_at: true,
        chat_type: true,
        view: true,
        email: true,
        download: true,
        delete: true,
      })
    } else {
      setColumnVisibility({
        name: true,
        started_at: false,
        chat_type: false,
        view: false,
        email: false,
        download: false,
        delete: false,
      })
    }
  }, [isDesktop])

  const table = useReactTable({
    debugTable: true,
    data: chatHistories,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    state: {
      sorting,
      columnVisibility,
      columnFilters,
    },
    onSortingChange: setSorting,
    getExpandedRowModel: getExpandedRowModel(),
    enableMultiSorting: true,
    meta: {
      setSelectedChat,
      setChatHistories,
      toast,
      setColumnFilters,
    },
  })

  return (
    <div className="rounded-2xl border border-alto-200">
      <Table tableClass="rounded-2xl">
        <TableHeader className="rounded-2xl bg-intoo-green-coachcta">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow
              key={`saved_job_header_${headerGroup.id}`}
              className="border-alto-200 bg-intoo-green-coachcta p-4 text-white hover:bg-intoo-green-coachcta sm:px-8 sm:py-6"
            >
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead
                    key={header.id}
                    className="job-search-table-header font-weight-700 h-16 font-roboto text-lg font-bold text-foreground data-[col-id=status]:w-[96px] sm:h-16 sm:px-4 data-[col-id=status]:sm:w-[120px]"
                    data-col-id={header.column.id}
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        className={cn(
                          'flex flex-wrap items-center gap-2',
                          header.column.getCanSort()
                            ? 'cursor-pointer select-none'
                            : '',
                        )}
                        onClick={header.column.getToggleSortingHandler()}
                        title={
                          header.column.getCanSort()
                            ? header.column.getNextSortingOrder() === 'asc'
                              ? t('Feedback.sort_ascending')
                              : header.column.getNextSortingOrder() === 'desc'
                                ? t('Feedback.sort_descending')
                                : t('Feedback.clear_sort')
                            : undefined
                        }
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}{' '}
                        {header.column.getCanSort() && <SortIcon />}
                      </div>
                    )}
                  </TableHead>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row, idx) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}
                fred={5}
                className={`${
                  idx % 2
                    ? 'bg-[#e2e2e2] bg-opacity-30 hover:bg-[#e2e2e2] hover:bg-opacity-30 data-[state=selected]:bg-[#e2e2e2] data-[state=selected]:bg-opacity-30'
                    : 'bg-white hover:bg-white data-[state=selected]:bg-white'
                } border-alto-200`}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className="pb-2 pt-3 sm:p-4 sm:pr-1">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24">
                <div className="flex flex-col items-center justify-center">
                  <img
                    src="https://www-yns-com.s3.amazonaws.com/images/coach_chat/chat_history_empty.png"
                    alt="chat history empty"
                  />
                  <Typography variant="subtitle" className="text-gray-600">
                    {t('chat_histories.no_chats')}
                  </Typography>
                  <Typography>
                    {t('chat_histories.no_chats_description')}
                  </Typography>
                  <Typography>
                    {parse(
                      t('chat_histories.no_chats_cta', {
                        link_to_live_chat: `<a href="#" class="open-chat-modal">${t('chat_histories.no_chats_cta_link')}</a>`,
                      }),
                      {
                        replace(node) {
                          if (
                            node.name === 'a' &&
                            node.attribs?.class?.includes('open-chat-modal')
                          ) {
                            return (
                              <button
                                className="underline"
                                onClick={() =>
                                  document
                                    .querySelector('.yns-btn-coach-chat-list')
                                    ?.click()
                                }
                              >
                                {domToReact(node.children)}
                              </button>
                            )
                          }
                        },
                      },
                    )}
                  </Typography>
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}

export default DataTable
